import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGetChargePointsPaginatedDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.form-default-values';
import { GetChargePointsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/get-charge-points-paginated.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';

import {
  FilterChargePointListContextProvider,
  useChargePointListContext,
} from '../charge-point.context';
import { ChargePointListFilterFormController } from './charge-point-list-filter-form/charge-point-list-filter-form.controller';

interface ChargePointListFilterControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointListFilterController = ({
  onCancel,
  onSuccess,
}: ChargePointListFilterControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const { getPaginatedQueryParameters, setGetPaginatedQueryParameters } =
    useChargePointListContext();
  const [multiStepFormState] = useState(MultiStepFormState.Default);

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<GetChargePointsPaginatedModel> = (
    errors,
  ) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-list-filter.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    GetChargePointsPaginatedModel
  > = (fieldValues) => {
    setGetPaginatedQueryParameters((current) => {
      return {
        ...current,
        // ?? undefined: the isOnline/isMaintenance controls use `null` as
        // their "All" sentinel (see charge-point-is-online-filter /
        // charge-point-is-maintenance-filter controllers) to work around a
        // react-hook-form Controller quirk — normalize back to `undefined`
        // here for the actual query parameters.
        isOnline: fieldValues.isOnline ?? undefined,
        channelOcppStatus: fieldValues.channelOcppStatus,
        isMaintenance: fieldValues.isMaintenance ?? undefined,
        customerId: fieldValues.customerId,
        hasTariff: fieldValues.hasTariff ?? undefined,
        hasLocation: fieldValues.hasLocation ?? undefined,
        hasSubscription: fieldValues.hasSubscription ?? undefined,
        lastMessageReceivedDate: fieldValues.lastMessageReceivedDate,
        locationId: fieldValues.locationId,
        networkId: fieldValues.networkId,
        tariffId: fieldValues.tariffId,
      };
    });
    onSuccess();
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getGetChargePointsPaginatedDefaultValues(
    getPaginatedQueryParameters,
  );

  return (
    <FilterChargePointListContextProvider data={data}>
      <ChargePointListFilterFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </FilterChargePointListContextProvider>
  );
};
